import type {
  DrawingDocument,
  DrawingEntity,
  DrawingPage,
  QualityIssue,
  Matrix3,
  ErrorCode,
  Point,
} from '../contract/types.js';
import { CONTRACT_VERSION } from '../contract/version.js';
import { Ledger } from './ledger.js';
import { splitSubPaths, bboxOf, subPathPoints, type PathSegment } from './subpath.js';
import { recogniseSubPath, IdFactory, type StyleContext } from './recognise.js';
import { extractTextEntities, detectOutlinedText } from './text.js';
import { multiply, applyTo, IDENTITY } from '../transform/matrix.js';

export const EXTRACTOR_BUNDLE_VERSION = '0.1.0';

/** Phase 1 input limits, from the board. */
export const DEFAULT_MAX_PAGES = 20;
export const DEFAULT_MAX_BYTES = 50 * 1024 * 1024;

export interface ExtractOptions {
  maxPages?: number;
  maxBytes?: number;
  /**
   * Location of pdf.js's character maps.
   *
   * **Required for Japanese text.** Without it, CJK fonts fail to load and
   * their text is dropped entirely — silently, with only a console warning.
   * On a Japanese mechanical drawing that is not a degraded result, it is a
   * missing one, and it looks exactly like a drawing that had no text.
   *
   * The core stays free of any filesystem assumption, so the caller supplies
   * the location: a directory path in Node, a bundled asset URL in a browser.
   */
  cMapUrl?: string;
  cMapPacked?: boolean;
  /** Location of the standard font data, for the base-14 PDF fonts. */
  standardFontDataUrl?: string;
  /**
   * Wall-clock budget for the whole extraction.
   *
   * Without one, `EXTRACTION_TIMEOUT` is a code that can never occur, and a
   * pathological file blocks the caller indefinitely. Zero fails immediately,
   * which is what makes the code testable.
   */
  timeoutMs?: number;
}

export const DEFAULT_TIMEOUT_MS = 30_000;

/** `%PDF-` — every PDF begins with it, within the first bytes of the file. */
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d];

function looksLikePdf(data: Uint8Array): boolean {
  // The specification allows leading junk before the header, so a short window
  // is scanned rather than only offset zero.
  const window = Math.min(data.length, 1024);
  for (let i = 0; i + PDF_MAGIC.length <= window; i++) {
    let ok = true;
    for (let j = 0; j < PDF_MAGIC.length; j++) {
      if (data[i + j] !== PDF_MAGIC[j]) {
        ok = false;
        break;
      }
    }
    if (ok) return true;
  }
  return false;
}

/** Failures are classified with a code from the closed set, never free text. */
export class ExtractionError extends Error {
  constructor(
    public readonly code: ErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ExtractionError';
  }
}

/** How a constructed path was painted, which decides whether it is geometry at all. */
type PathFate = 'painted' | 'clipping' | 'discarded';

interface PendingPath {
  segments: PathSegment[];
  operatorIndex: number;
  clipping: boolean;
}

type PdfJs = typeof import('pdfjs-dist/legacy/build/pdf.mjs');

let cachedPdfJs: PdfJs | null = null;

/**
 * Loads pdf.js, once per process.
 *
 * Exported so a browser host can configure the worker source on the same module
 * instance before extraction runs. The core sets no worker itself, because
 * where the worker lives is the host's business, not the engine's.
 */
export async function loadPdfJs(): Promise<PdfJs> {
  cachedPdfJs ??= (await import('pdfjs-dist/legacy/build/pdf.mjs')) as PdfJs;
  return cachedPdfJs;
}

/**
 * Reads a PDF into Drawing2D.
 *
 * The result is always at **unknown scale**. Extraction cannot know the physical
 * scale of a drawing, so it does not claim to: resolving it is a separate,
 * explicit step, and until it happens no DXF can be written.
 */
export async function extractDocument(
  data: Uint8Array,
  options: ExtractOptions = {},
): Promise<DrawingDocument<'unknown'>> {
  const maxPages = options.maxPages ?? DEFAULT_MAX_PAGES;
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const deadline = Date.now() + timeoutMs;

  const checkDeadline = (): void => {
    if (Date.now() >= deadline) {
      throw new ExtractionError(
        'EXTRACTION_TIMEOUT',
        `extraction exceeded its budget of ${timeoutMs} ms`,
      );
    }
  };

  if (data.byteLength > maxBytes) {
    throw new ExtractionError(
      'PDF_LIMIT_EXCEEDED',
      `file is ${data.byteLength} bytes, limit is ${maxBytes}`,
    );
  }

  // A file that is not a PDF at all is a different outcome from a PDF that
  // cannot be parsed, and the board gives each its own code.
  if (!looksLikePdf(data)) {
    throw new ExtractionError(
      'PDF_UNSUPPORTED_MEDIA_TYPE',
      'the file does not carry a PDF header',
    );
  }

  checkDeadline();
  const pdfjs = await loadPdfJs();
  const ledger = new Ledger();
  const issues: QualityIssue[] = [];

  let pdf: Awaited<ReturnType<PdfJs['getDocument']>['promise']>;
  try {
    pdf = await pdfjs.getDocument({
      // pdf.js takes *ownership* of the buffer it is given and detaches it, so
      // the caller's array becomes unusable the moment extraction starts. That
      // is a surprise nobody should have to discover: a caller extracting the
      // same drawing twice, or rendering it after extracting, gets a detached
      // ArrayBuffer error with no obvious cause. It gets its own copy.
      data: data.slice(),
      isEvalSupported: false,
      useSystemFonts: false,
      // The extraction path makes no network calls. Character maps and font
      // data are read from locations the caller supplies, not fetched.
      disableFontFace: true,
      ...(options.cMapUrl !== undefined ? { cMapUrl: options.cMapUrl } : {}),
      cMapPacked: options.cMapPacked ?? true,
      ...(options.standardFontDataUrl !== undefined
        ? { standardFontDataUrl: options.standardFontDataUrl }
        : {}),
    }).promise;
  } catch (err) {
    throw classifyOpenError(err);
  }

  if (pdf.numPages > maxPages) {
    throw new ExtractionError(
      'PDF_LIMIT_EXCEEDED',
      `page count ${pdf.numPages} exceeds the limit of ${maxPages}`,
    );
  }

  const pages: DrawingPage[] = [];
  for (let i = 0; i < pdf.numPages; i++) {
    checkDeadline();
    pages.push(await extractPage(pdfjs, pdf, i, ledger, issues));
  }

  // Nothing above has determined physical scale, so the document says so.
  issues.push({
    kind: 'undetermined_scale',
    count: 1,
    pageIndex: 0,
    detail:
      'physical scale not determined during extraction; resolve from a manifest declaration, the title block, or manual calibration',
  });

  ledger.assertReconciled();

  return {
    contractVersion: CONTRACT_VERSION,
    scaleState: 'unknown',
    unit: 'unknown',
    mmPerPoint: null,
    pages,
    provenance: {
      transform: IDENTITY,
      inverse: IDENTITY,
      sourceSpace: 'pdf_pt',
      // Page y increases downward, so a rising angle turns clockwise on the sheet.
      angleSense: 'clockwise_on_page',
      targetSpace: 'pdf_pt',
      libraryVersions: { 'pdfjs-dist': pdfjs.version },
      extractorBundleVersion: EXTRACTOR_BUNDLE_VERSION,
    },
    qualityIssues: issues,
    ledger: ledger.snapshot(),
  };
}

function classifyOpenError(err: unknown): ExtractionError {
  const name = (err as { name?: string }).name ?? '';
  const message = (err as { message?: string }).message ?? String(err);
  if (name === 'PasswordException') {
    // Encrypted drawings are common in practice and fail before any geometry is
    // read, so they get their own code rather than being called corruption.
    return new ExtractionError('PDF_ENCRYPTED', 'the PDF is encrypted and cannot be opened');
  }
  if (name === 'InvalidPDFException' || /invalid pdf/i.test(message)) {
    return new ExtractionError('PDF_CORRUPT', `the PDF cannot be parsed: ${message}`);
  }
  return new ExtractionError('PDF_CORRUPT', `the PDF cannot be parsed: ${message}`);
}

async function extractPage(
  pdfjs: PdfJs,
  pdf: Awaited<ReturnType<PdfJs['getDocument']>['promise']>,
  index: number,
  ledger: Ledger,
  issues: QualityIssue[],
): Promise<DrawingPage> {
  const page = await pdf.getPage(index + 1);
  const viewport = page.getViewport({ scale: 1 });
  const opList = await page.getOperatorList();
  const OPS = pdfjs.OPS as unknown as Record<string, number>;

  /**
   * The viewport transform maps PDF user space to page space in one matrix:
   * the y flip, the crop-box offset and the page rotation together. Doing it in
   * three separate steps is how crop boxes and rotated pages get mishandled.
   *
   * A crop box offset from the media box legitimately produces negative
   * coordinates, so nothing here clamps them.
   */
  const base = viewport.transform as unknown as Matrix3;

  const entities: DrawingEntity[] = [];
  const style: StyleContext = {
    layer: '0',
    strokeWidth: 0.25,
    colour: '000000',
    ids: new IdFactory(`p${index}`),
  };

  let ctm: Matrix3 = IDENTITY;
  const ctmStack: Matrix3[] = [];
  const layerStack: string[] = [];
  let pending: PendingPath | null = null;
  let textRenderMode = 0;
  let invisibleTextRuns = 0;
  const closedSmallPaths: (readonly [number, number, number, number])[] = [];

  /**
   * Fonts the page names, and whether the file carries the program for them.
   *
   * pdf.js resolves each `setFont` into a font object on `commonObjs`, and sets
   * `missingFile` when it had to substitute because the font program is not
   * embedded. That is the only place in this pipeline the fact is visible, and
   * it is visible here for free because the operator list has already been
   * fetched.
   *
   * Keyed by the *declared* name rather than by pdf.js's internal handle, so a
   * font set eighteen times is reported once. What matters to the person who
   * exported the drawing is which typeface to embed, not how often it was used.
   */
  const seenFonts = new Set<string>();
  const recordFont = (handle: string): void => {
    interface LoadedFont {
      name?: string;
      missingFile?: boolean;
    }
    let font: LoadedFont | null = null;
    try {
      font = (page as unknown as { commonObjs: { get(name: string): LoadedFont | null } }).commonObjs.get(
        handle,
      );
    } catch {
      // A handle the object store does not know is not evidence of anything.
      return;
    }
    if (!font || font.missingFile !== true) return;
    const name = font.name ?? handle;
    if (seenFonts.has(name)) return;
    seenFonts.add(name);
    issues.push({
      kind: 'font_not_embedded',
      count: 1,
      pageIndex: index,
      detail:
        `${name} is named by the page and its font program is not in the file; ` +
        'the reader substitutes, so glyph widths and appearance are the reader\'s, not the drawing\'s',
    });
  };

  const resolvePending = (fate: PathFate): void => {
    if (!pending) return;
    const world = pending.segments.map((s) => mapSegment(s, multiply(base, ctm)));

    if (fate !== 'painted') {
      // A clipping path or an abandoned path is not drawing geometry. It is
      // still counted, so the ledger stays reconciled.
      const count = Math.max(world.filter((s) => s.kind !== 'move').length, 1);
      ledger.record(fate === 'clipping' ? 'clip' : 'endPath', count);
      issues.push({
        kind: 'unsupported_operator',
        count,
        pageIndex: index,
        bbox: bboxOf(subPathPoints(world)),
        operator: fate === 'clipping' ? 'clip' : 'endPath',
        detail:
          fate === 'clipping'
            ? 'path used as a clipping region rather than drawn geometry'
            : 'path constructed but never painted',
      });
      pending = null;
      return;
    }

    for (const sub of splitSubPaths(world)) {
      const result = recogniseSubPath(
        sub,
        style,
        { pageIndex: index, operatorIndex: pending.operatorIndex, bbox: [0, 0, 0, 0] },
        ledger,
      );
      entities.push(...result.entities);
      issues.push(...result.issues);

      const pts = subPathPoints(sub);
      const bb = bboxOf(pts);
      const diagonal = Math.hypot(bb[2] - bb[0], bb[3] - bb[1]);
      if (diagonal > 0 && diagonal < 15 && sub.some((s) => s.kind === 'close')) {
        closedSmallPaths.push(bb);
      }
    }
    pending = null;
  };

  // pdf.js indexes its operators by number; the ledger classifies them by name,
  // so the lookup is built once per page rather than searched per operator.
  const operatorName = new Map<number, string>();
  for (const [name, code] of Object.entries(OPS)) operatorName.set(code, name);

  for (let i = 0; i < opList.fnArray.length; i++) {
    const fn = opList.fnArray[i]!;
    const args = opList.argsArray[i] as unknown[];

    // Counted before anything is decided about it, and counted for every
    // operator including the ones this extractor has nothing to do with. This
    // is the figure `consumed` and `recorded` are checked against; without it
    // the ledger was comparing an expression with itself.
    ledger.observe(operatorName.get(fn) ?? `unknown_${String(fn)}`);

    switch (fn) {
      case OPS['save']:
        ctmStack.push(ctm);
        continue;
      case OPS['restore']:
        ctm = ctmStack.pop() ?? IDENTITY;
        continue;
      case OPS['transform']:
        ctm = multiply(ctm, args as unknown as Matrix3);
        continue;
      case OPS['setLineWidth']:
        style.strokeWidth = toPositive(args[0], 0.25);
        continue;
      case OPS['setStrokeRGBColor']:
      case OPS['setFillRGBColor']:
        style.colour = rgbHex(args);
        continue;
      case OPS['setStrokeGray']:
      case OPS['setFillGray']:
        style.colour = grayHex(args[0]);
        continue;
      case OPS['setTextRenderingMode']:
        textRenderMode = Number(args[0]) || 0;
        continue;
      case OPS['beginMarkedContentProps']: {
        // Optional content groups are the closest thing a PDF has to layers.
        const tag = typeof args[0] === 'string' ? args[0] : null;
        layerStack.push(style.layer);
        if (tag && tag !== 'OC') style.layer = tag;
        continue;
      }
      case OPS['endMarkedContent']:
        style.layer = layerStack.pop() ?? '0';
        continue;
      case OPS['constructPath']: {
        // A single PDF path can arrive as several constructPath operators —
        // pdf.js flushes its buffer part-way through, and unrelated operators
        // such as setLineWidth appear between the pieces. They accumulate into
        // one path, which is consumed only when a paint, clip or end operator
        // arrives. Treating each one as a separate path throws away everything
        // but the last piece.
        const segments = readPath(args, OPS);
        if (pending) pending.segments.push(...segments);
        else pending = { segments, operatorIndex: i, clipping: false };
        continue;
      }
      case OPS['clip']:
      case OPS['eoClip']:
        if (pending) pending.clipping = true;
        continue;
      case OPS['endPath']:
        resolvePending(pending?.clipping ? 'clipping' : 'discarded');
        continue;
      case OPS['setFont']:
        recordFont(args[0] as string);
        continue;
      case OPS['showText']:
      case OPS['showSpacedText']:
        // Render mode 3 is invisible and 7 is clip-only: the signature of a text
        // layer laid over a scan by recognition software.
        if (textRenderMode === 3 || textRenderMode === 7) invisibleTextRuns += 1;
        continue;
      case OPS['paintImageXObject']:
      case OPS['paintInlineImageXObject']:
      case OPS['paintImageMaskXObject']:
        ledger.record('paintImage');
        issues.push({
          kind: 'unsupported_operator',
          count: 1,
          pageIndex: index,
          operator: 'paintImage',
          detail: 'raster image on this page; vector extraction does not read inside it',
        });
        continue;
      default:
        break;
    }

    if (isPaintOp(fn, OPS)) resolvePending('painted');
  }

  resolvePending('discarded');

  if (invisibleTextRuns > 0) {
    issues.push({
      kind: 'invisible_text',
      count: invisibleTextRuns,
      pageIndex: index,
      detail: 'text drawn in an invisible render mode, typical of a recognition layer over a scan',
    });
  }

  const text = await extractTextEntities(page, index, base, style, ledger);
  entities.push(...text.entities);
  issues.push(...text.issues);

  const outlined = detectOutlinedText(closedSmallPaths, text.entities.length, index);
  if (outlined) issues.push(outlined);

  return {
    index,
    widthPt: viewport.width,
    heightPt: viewport.height,
    rotation: normaliseRotation(page.rotate),
    entities,
    // Identity until scale is resolved: extraction cannot know the physical
    // scale, so it does not pretend to carry a transform into drawing space.
    transform: IDENTITY,
    inverse: IDENTITY,
  };
}

const PAINT_OP_NAMES = [
  'stroke',
  'closeStroke',
  'fill',
  'eoFill',
  'fillStroke',
  'eoFillStroke',
  'closeFillStroke',
  'closeEOFillStroke',
] as const;

function isPaintOp(fn: number, OPS: Record<string, number>): boolean {
  return PAINT_OP_NAMES.some((name) => OPS[name] === fn);
}

/** Reads a constructPath operator into neutral segments. */
function readPath(args: unknown[], OPS: Record<string, number>): PathSegment[] {
  const ops = (args[0] ?? []) as number[];
  const coords = (args[1] ?? []) as number[];
  const out: PathSegment[] = [];
  let c = 0;
  const take = (): number => coords[c++] ?? 0;

  for (const op of ops) {
    if (op === OPS['moveTo']) {
      out.push({ kind: 'move', to: { x: take(), y: take() } });
    } else if (op === OPS['lineTo']) {
      out.push({ kind: 'line', to: { x: take(), y: take() } });
    } else if (op === OPS['curveTo']) {
      out.push({
        kind: 'curve',
        c1: { x: take(), y: take() },
        c2: { x: take(), y: take() },
        to: { x: take(), y: take() },
      });
    } else if (op === OPS['curveTo2']) {
      // The `v` operator: the first control point is the current point.
      const current = lastPointOf(out);
      const c2 = { x: take(), y: take() };
      const to = { x: take(), y: take() };
      out.push({ kind: 'curve', c1: current, c2, to });
    } else if (op === OPS['curveTo3']) {
      // The `y` operator: the second control point is the endpoint.
      const c1 = { x: take(), y: take() };
      const to = { x: take(), y: take() };
      out.push({ kind: 'curve', c1, c2: { ...to }, to });
    } else if (op === OPS['closePath']) {
      out.push({ kind: 'close' });
    } else if (op === OPS['rectangle']) {
      const x = take();
      const y = take();
      const w = take();
      const h = take();
      out.push(
        { kind: 'move', to: { x, y } },
        { kind: 'line', to: { x: x + w, y } },
        { kind: 'line', to: { x: x + w, y: y + h } },
        { kind: 'line', to: { x, y: y + h } },
        { kind: 'close' },
      );
    }
  }
  return out;
}

function lastPointOf(segments: PathSegment[]): Point {
  for (let i = segments.length - 1; i >= 0; i--) {
    const s = segments[i]!;
    if (s.kind !== 'close') return { ...s.to };
  }
  return { x: 0, y: 0 };
}

function mapSegment(s: PathSegment, m: Matrix3): PathSegment {
  if (s.kind === 'close') return s;
  if (s.kind === 'curve') {
    return { kind: 'curve', c1: applyTo(m, s.c1), c2: applyTo(m, s.c2), to: applyTo(m, s.to) };
  }
  return { kind: s.kind, to: applyTo(m, s.to) };
}

function toPositive(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function channel(v: unknown): string {
  const n = Number(v);
  const scaled = n <= 1 ? n * 255 : n;
  return Math.max(0, Math.min(255, Math.round(scaled))).toString(16).padStart(2, '0');
}

function rgbHex(args: unknown[]): string {
  return `${channel(args[0])}${channel(args[1])}${channel(args[2])}`;
}

function grayHex(v: unknown): string {
  const g = channel(v);
  return `${g}${g}${g}`;
}

function normaliseRotation(r: number): 0 | 90 | 180 | 270 {
  const n = ((Math.round(r / 90) * 90) % 360 + 360) % 360;
  return (n === 90 || n === 180 || n === 270 ? n : 0) as 0 | 90 | 180 | 270;
}
